import type {
  IPriceProductQuickFillOperator,
  IPriceProductQuickFillRule,
  IPriceProductQuickFillUnit,
} from '../types';
import type { IVariantPriceRange } from '../../../utils';

import {
  PRICE_PRODUCT_PERCENT_BASE,
  PRICE_PRODUCT_QUICK_FILL_LABEL,
  PRICE_PRODUCT_QUICK_FILL_OPERATOR,
  PRICE_PRODUCT_QUICK_FILL_UNIT,
} from '../constants';
import { cleanDecimal } from '~/utils/number';

const QUICK_FILL_OPERATORS = Object.values(PRICE_PRODUCT_QUICK_FILL_OPERATOR);
const QUICK_FILL_UNITS = Object.values(PRICE_PRODUCT_QUICK_FILL_UNIT);

// Polaris' Select hands its onChange a plain string; matching against the set narrows it without an
// assertion, and an unrecognised value falls out as undefined rather than becoming a bad rule.
export const toQuickFillOperator = (value: string): IPriceProductQuickFillOperator | undefined =>
  QUICK_FILL_OPERATORS.find((operator) => operator === value);

export const toQuickFillUnit = (value: string): IPriceProductQuickFillUnit | undefined =>
  QUICK_FILL_UNITS.find((unit) => unit === value);

export interface IToQuickFillLabelParams {
  isQuickFillOpen: boolean;
  isAnySelected: boolean;
}

/**
 * What the button says. It names what it is about to do rather than where it is: the reach of a rule
 * is the one thing the bar itself never shows, so the button is where a selection has to be spelled out.
 */
export const toQuickFillLabel = ({
  isQuickFillOpen,
  isAnySelected,
}: IToQuickFillLabelParams): string => {
  if (isQuickFillOpen) return PRICE_PRODUCT_QUICK_FILL_LABEL.APPLY;
  if (isAnySelected) return PRICE_PRODUCT_QUICK_FILL_LABEL.SELECTED;

  return PRICE_PRODUCT_QUICK_FILL_LABEL.IDLE;
};

/** The one amount a rule starts from. A variant's Control range only ever carries its single price. */
export const toControlAmount = (range?: IVariantPriceRange): number | undefined => {
  if (!range) return undefined;

  const amount = Number(range.min);

  return Number.isFinite(amount) ? amount : undefined;
};

export interface IApplyQuickFillRuleParams {
  rule: IPriceProductQuickFillRule;
  /** What the variant sells for now. Absent for a product Shopify returned no price for. */
  controlAmount?: number;
}

/**
 * What a rule would write against one variant, or undefined when it has nothing to say — an empty
 * amount, or a relative rule against a variant whose Control price never arrived. Undefined is what
 * leaves an already typed amount standing, so an emptied rule puts every field back as it was.
 */
export const applyQuickFillRule = ({
  rule,
  controlAmount,
}: IApplyQuickFillRuleParams): string | undefined => {
  const amount = Number(rule.value);
  if (!rule.value || !Number.isFinite(amount)) return undefined;

  if (rule.operator === PRICE_PRODUCT_QUICK_FILL_OPERATOR.SET) return cleanDecimal(amount);
  if (controlAmount === undefined) return undefined;

  const isPercent = rule.unit === PRICE_PRODUCT_QUICK_FILL_UNIT.PERCENT;
  const delta = isPercent ? (controlAmount * amount) / PRICE_PRODUCT_PERCENT_BASE : amount;
  const isDecrease = rule.operator === PRICE_PRODUCT_QUICK_FILL_OPERATOR.DECREASE;

  // Clamped: a discount larger than the price would otherwise offer the merchant a negative amount,
  // which Shopify rejects anyway.
  return cleanDecimal(Math.max(controlAmount + (isDecrease ? -delta : delta), 0));
};
